import { Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute, { PublicOnlyRoute } from "./ProtectedRoute";
import RoleGate from "./RoleGate";
import { VIEWS, FEATURES, ROLES } from "../services/navigation";

import PmsLogin from "../pages/auth/PmsLogin";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import AcceptInvitationPage from "../pages/auth/AcceptInvitationPage";
import DashboardPage from "../pages/dashboard/DashboardPage";

import MyGoalsPage from "../pages/employee/MyGoalsPage";
import MyKraKpiPage from "../pages/employee/MyKraKpiPage";
import SelfAssessmentPage from "../pages/employee/SelfAssessmentPage";
import MyResultsPage from "../pages/employee/MyResultsPage";

import TeamPage from "../pages/manager/TeamPage";
import TeamMemberPage from "../pages/manager/TeamMemberPage";
import GoalApprovalsPage from "../pages/manager/GoalApprovalsPage";
import GoalApprovalReviewPage from "../pages/manager/GoalApprovalReviewPage";
import TeamAssessmentsPage from "../pages/manager/TeamAssessmentsPage";
import ManagerAssessmentPage from "../pages/manager/ManagerAssessmentPage";

import EmployeeMasterPage from "../pages/hr/EmployeeMasterPage";
import PerformanceCyclesPage from "../pages/hr/PerformanceCyclesPage";
import KraKpiAssignmentPage from "../pages/hr/KraKpiAssignmentPage";
import HrReviewPage from "../pages/hr/HrReviewPage";
import HrReviewDetailPage from "../pages/hr/HrReviewDetailPage";
import RatingSettingsPage from "../pages/hr/RatingSettingsPage";
import OrganisationSetupPage from "../pages/hr/OrganisationSetupPage";

import RemindersPage from "../pages/hr/RemindersPage";

import CompetencyPage from "../pages/performance/CompetencyPage";
import PipPage from "../pages/performance/PipPage";
import CalibrationPage from "../pages/performance/CalibrationPage";
import FeedbackPage from "../pages/performance/FeedbackPage";

import UserRolesPage from "../pages/admin/UserRolesPage";
import AuditTrailPage from "../pages/admin/AuditTrailPage";
import MonitoringPage from "../pages/admin/MonitoringPage";
import { NoTableTools } from "../components/common/DataTable"; // [NEW] no Export / Import on Admin menu pages

import ReportsPage from "../pages/shared/ReportsPage";
import NotificationsPage from "../pages/shared/NotificationsPage";
import ProfilePage from "../pages/shared/ProfilePage";
import UserManualPage from "../pages/shared/UserManualPage";
import HelpSupportPage from "../pages/shared/HelpSupportPage"; // [NEW]
import StyleGuidePage from "../pages/shared/StyleGuidePage";
import NotFoundPage from "../pages/errors/NotFoundPage";

import KraKpiProposalsPage from "../pages/manager/KraKpiProposalsPage";
import ProgressApprovalsPage from "../pages/manager/ProgressApprovalsPage";
import EvidenceSearchPage from "../pages/shared/EvidenceSearchPage";
import AppraisalPage from "../pages/performance/AppraisalPage"; // [NEW] requirement 7
import MyActivityPage from "../pages/employee/MyActivityPage"; // [NEW] PM-8

const { EMPLOYEE, MANAGER, HR, MANAGEMENT, ADMIN } = VIEWS;

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><PmsLogin /></PublicOnlyRoute>} />
      <Route path="/login/:loginSlug" element={<PublicOnlyRoute><PmsLogin /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

      <Route element={<ProtectedRoute />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/emp/dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* [NEW] Help & support, open to every signed-in user */}
        <Route path="/help" element={<HelpSupportPage />} />
        <Route path="/help/user-manual" element={<UserManualPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />

        <Route path="/competencies" element={FEATURES.competency ? <CompetencyPage /> : <NotFoundPage />} />
        <Route path="/pip" element={<PipPage />} />
        {/* [NEW] Requirement 7: Appraisal (HR / Admin: everybody, managers: their team, employees: own decision). */}
        <Route element={<RoleGate views={[HR, ADMIN, MANAGER, EMPLOYEE]} />}>
          <Route path="/appraisal" element={<AppraisalPage />} />
        </Route>
        <Route path="/feedback" element={FEATURES.feedback ? <FeedbackPage /> : <NotFoundPage />} />
        {/* [UPDATED] Requirement 4: evidence is for employees (own) and managers (team) only, not for the HR view. */}
        <Route element={<RoleGate views={[MANAGER, EMPLOYEE]} />}>
          <Route path="/evidence" element={<EvidenceSearchPage />} />
        </Route>

        <Route element={<RoleGate views={[EMPLOYEE]} />}>
          <Route path="/my-goals" element={<NoTableTools><MyGoalsPage /></NoTableTools>} />
          <Route path="/my-kra-kpi" element={<MyKraKpiPage />} />
          <Route path="/self-assessment" element={<SelfAssessmentPage />} />
          <Route path="/my-results" element={<MyResultsPage />} />
          <Route path="/my-activity" element={<MyActivityPage />} /> {/* [NEW] PM-8 */}
        </Route>

        <Route element={<RoleGate views={[MANAGER]} />}>
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/goal-approvals" element={<GoalApprovalsPage />} />
          <Route path="/team/goal-approvals/:employeeId" element={<GoalApprovalReviewPage />} />
          <Route path="/team/assessments" element={<TeamAssessmentsPage />} />
          <Route path="/team/assessments/:employeeId" element={<ManagerAssessmentPage />} />
          {/* [UPDATED] PM-17: managers no longer assign or own KRA/KPI definitions; old links open the approvals. */}
          <Route path="/team/kra-kpi" element={<Navigate to="/team/kra-kpi-proposals" replace />} />
          <Route path="/team/kra-library" element={<Navigate to="/team/kra-kpi-proposals" replace />} />
          <Route path="/team/kpi-library" element={<Navigate to="/team/kra-kpi-proposals" replace />} />
          <Route path="/team/role-templates" element={<Navigate to="/team/kra-kpi-proposals" replace />} />
          <Route path="/team/reminders" element={<RemindersPage scope="team" />} />
          <Route path="/team/progress-approvals" element={<ProgressApprovalsPage />} />
          <Route path="/team/kra-kpi-proposals" element={<KraKpiProposalsPage />} />
          <Route path="/team/:employeeId" element={<TeamMemberPage />} />
        </Route>

        <Route element={<RoleGate views={[HR]} />}>
          <Route path="/hr/employees" element={<EmployeeMasterPage />} />
          {/* [UPDATED] KRA/KPI libraries and role templates are no longer in the menu; old links open the review page. */}
          <Route path="/hr/kra-master" element={<Navigate to="/hr/kra-kpi" replace />} />
          <Route path="/hr/kpi-master" element={<Navigate to="/hr/kra-kpi" replace />} />
          <Route path="/hr/kra-kpi" element={<KraKpiAssignmentPage scope="org" />} />
          <Route path="/hr/role-templates" element={<Navigate to="/hr/kra-kpi" replace />} />
          <Route path="/hr/reminders" element={<RemindersPage scope="org" />} />
          <Route path="/hr/review" element={<HrReviewPage />} />
          <Route path="/hr/review/:employeeId" element={<HrReviewDetailPage />} />
        </Route>

        <Route element={<RoleGate views={[HR, MANAGEMENT]} />}>
          <Route path="/calibration" element={FEATURES.calibration ? <CalibrationPage /> : <NotFoundPage />} />
        </Route>

        <Route element={<RoleGate views={[HR, ADMIN]} />}>
          <Route path="/hr/cycles" element={<PerformanceCyclesPage />} />
          <Route path="/hr/rating-settings" element={<NoTableTools><RatingSettingsPage /></NoTableTools>} />
          <Route path="/hr/organisation" element={<NoTableTools><OrganisationSetupPage /></NoTableTools>} />
        </Route>

        <Route element={<RoleGate views={[HR, MANAGEMENT, MANAGER]} />}>
          <Route path="/reports" element={<ReportsPage />} />
        </Route>

        <Route element={<RoleGate views={[ADMIN]} roles={[ROLES.IT_ADMIN]} />}>
          {/* HR, managers and employees see only their own history under My activity. */}
          <Route path="/audit-trail" element={<NoTableTools><AuditTrailPage /></NoTableTools>} />
        </Route>

        <Route element={<RoleGate views={[ADMIN]} />}>
          <Route path="/monitoring" element={<NoTableTools><MonitoringPage /></NoTableTools>} />
        </Route>

        <Route element={<RoleGate views={[ADMIN]} />}>
          <Route path="/admin/users" element={<NoTableTools><UserRolesPage /></NoTableTools>} />
          <Route path="/style-guide" element={<StyleGuidePage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
